/* RESPONSIBLE TEAM: team-inbox */
import AdminSummary, { type AdminSummaryWireFormat, AdminStatus } from './admin-summary';
import { REDACTED_ADMIN_NAME, REDACTED_ADMIN_URL } from 'embercom/lib/inbox/constants';
import {
  PRICING_5_X_LITE_SEAT_TYPE,
  COLLABORATOR_SEAT_TYPE,
  COPILOT_SEAT_TYPE,
  type ALL_SEAT_TYPES,
} from 'embercom/lib/settings/seats/constants';
import type { TeamAccessControlData } from 'embercom/services/bpo-access-control';

export enum ConversationAccessType {
  All = 'all',
  AllExcept = 'all_except',
  OnlyThem = 'only_them',
  OnlyThemOrTheirTeams = 'only_them_or_their_teams',
}

type SeatType = (typeof ALL_SEAT_TYPES)[number];

export interface AdminWithPermissionsWireFormat extends AdminSummaryWireFormat {
  team_ids: Array<number>;
  team_access_control?: TeamAccessControlData;
  permissions: {
    conversation_access: {
      access_type?: ConversationAccessType;
      assignee_blocked_list?: Array<number>;
      include_unassigned?: boolean;
    };
    has_inbox_access: boolean;
    manage_teammates: boolean;
    teammates__away_mode__change: boolean;
    inbox__conversation_transcripts__export: boolean;
    manage_tags: boolean;
    manage_saved_replies: boolean;
    create_macros: boolean;
    edit_macros: boolean;
    delete_macros: boolean;
    has_full_access: boolean;
    manage_inbox_views: boolean;
    manage_inbox_views_pinned_columns: boolean;
    manage_shared_custom_folders: boolean;
    access_real_time_dashboard: boolean;
    reassign_conversations: boolean;
    redact_conversation_parts: boolean;
    send_from_custom_addresses: boolean;
    access_workspace_settings: boolean;
    manage_emojis: boolean;
    manage_conversation_participants: boolean;
    manage_conversation_creation: boolean;
    merge_conversations: boolean;
    reply_to_inbound_conversations: boolean;
    access_contacts: boolean;
    access_billing_settings: boolean;
    can_access_all_conversations: boolean;
    calling__listening_and_barging: boolean;
    manage_knowledge_base_content: boolean;
    remove_conversation_slas: boolean;
    access_reporting: boolean;
    use_personal_macros: boolean;
    edit_own_profile: boolean;
    edit_other_profiles: boolean;
    role_id: number | null;
    inbox__access_copilot: boolean;
    calling__delete_call_recordings: boolean;
    use_data_connectors: boolean;
    reassign_conversations_on_away: boolean;
    change_ticket_type: boolean;
    edit_notes: boolean;
  };
  locale: string;
  teammate_preferred_locale?: string;

  seats: Array<SeatType | undefined>;
  admin_status?: {
    away_mode_enabled: boolean;
    reassign_conversations: boolean;
    is_auto_changed: boolean;
    channel_availability: string;
  };
  count?: number;
  inbox_2_opt_in?: boolean;
  copilot_waitlist_opt_in: boolean;
  is_operator?: boolean;
}

type ConversationAccess = {
  accessType?: ConversationAccessType;
  assigneeBlockedList?: Array<number>;
  includeUnassigned?: boolean;
};

export class AdminPermissions {
  conversationAccess: ConversationAccess;
  roleId: number | null;
  hasInboxAccess: boolean;
  canManageTeammates: boolean;
  canChangeAwayMode: boolean;
  canManageTags: boolean;
  canManageMacros: boolean;
  canCreateMacros: boolean;
  canEditMacros: boolean;
  canDeleteMacros: boolean;
  hasFullAccess: boolean;
  canManageInboxViews: boolean;
  canManageInboxViewsPinnedColumns: boolean;
  canManageSharedCustomFolders: boolean;
  canInboxAccessCopilot: boolean;
  canInboxConversationTranscriptsExport: boolean;
  canAccessRealTimeDashboard: boolean;
  canReassignConversations: boolean;
  canRedactConversationParts: boolean;
  canSendFromCustomAddresses: boolean;
  canAccessWorkspaceSettings: boolean;
  canManageEmojis: boolean;
  canManageConversationParticipants: boolean;
  canManageConversationCreation: boolean;
  canMergeConversations: boolean;
  canReplyToInboundConversations: boolean;
  canAccessContacts: boolean;
  canAccessBillingSettings: boolean;
  canAccessAllConversations: boolean;
  canListenAndBargeOnCalls: boolean;
  canManageKnowledgeBaseContent: boolean;
  canRemoveConversationSlas: boolean;
  canAccessReporting: boolean;
  canUsePersonalMacros: boolean;
  canDeleteCallRecordings: boolean;
  canUseDataConnectors: boolean;
  canReassignConversationsOnAway: boolean;
  canChangeTicketType: boolean;
  canEditNotes: boolean;
  canEditOwnProfile: boolean;
  canEditOtherProfiles: boolean;

  constructor(permissions: {
    conversationAccess: ConversationAccess;
    roleId: number | null;
    hasInboxAccess: boolean;
    canManageTeammates: boolean;
    canChangeAwayMode: boolean;
    canManageTags: boolean;
    canInboxAccessCopilot: boolean;
    canInboxConversationTranscriptsExport: boolean;
    canManageMacros: boolean;
    canCreateMacros: boolean;
    canEditMacros: boolean;
    canDeleteMacros: boolean;
    hasFullAccess: boolean;
    canManageInboxViews: boolean;
    canManageInboxViewsPinnedColumns: boolean;
    canManageSharedCustomFolders: boolean;
    canAccessRealTimeDashboard: boolean;
    canReassignConversations: boolean;
    canRedactConversationParts: boolean;
    canSendFromCustomAddresses: boolean;
    canAccessWorkspaceSettings: boolean;
    canManageEmojis: boolean;
    canManageConversationParticipants: boolean;
    canManageConversationCreation: boolean;
    canMergeConversations: boolean;
    canReplyToInboundConversations: boolean;
    canAccessContacts: boolean;
    canAccessBillingSettings: boolean;
    canAccessAllConversations: boolean;
    canListenAndBargeOnCalls: boolean;
    canManageKnowledgeBaseContent: boolean;
    canRemoveConversationSlas: boolean;
    canAccessReporting: boolean;
    canUsePersonalMacros: boolean;
    canDeleteCallRecordings: boolean;
    canUseDataConnectors: boolean;
    canReassignConversationsOnAway: boolean;
    canChangeTicketType: boolean;
    canEditNotes: boolean;
    canEditOwnProfile: boolean;
    canEditOtherProfiles: boolean;
  }) {
    this.conversationAccess = permissions.conversationAccess;
    this.roleId = permissions.roleId;
    this.hasInboxAccess = permissions.hasInboxAccess;
    this.canManageTeammates = permissions.canManageTeammates;
    this.canChangeAwayMode = permissions.canChangeAwayMode;
    this.canManageTags = permissions.canManageTags;
    this.canInboxAccessCopilot = permissions.canInboxAccessCopilot;
    this.canInboxConversationTranscriptsExport = permissions.canInboxConversationTranscriptsExport;
    this.canManageMacros = permissions.canManageMacros;
    this.canCreateMacros = permissions.canCreateMacros;
    this.canEditMacros = permissions.canEditMacros;
    this.canDeleteMacros = permissions.canDeleteMacros;
    this.hasFullAccess = permissions.hasFullAccess;
    this.canManageInboxViews = permissions.canManageInboxViews;
    this.canManageInboxViewsPinnedColumns = permissions.canManageInboxViewsPinnedColumns;
    this.canManageSharedCustomFolders = permissions.canManageSharedCustomFolders;
    this.canAccessRealTimeDashboard = permissions.canAccessRealTimeDashboard;
    this.canReassignConversations = permissions.canReassignConversations;
    this.canRedactConversationParts = permissions.canRedactConversationParts;
    this.canSendFromCustomAddresses = permissions.canSendFromCustomAddresses;
    this.canAccessWorkspaceSettings = permissions.canAccessWorkspaceSettings;
    this.canManageEmojis = permissions.canManageEmojis;
    this.canManageConversationParticipants = permissions.canManageConversationParticipants;
    this.canManageConversationCreation = permissions.canManageConversationCreation;
    this.canMergeConversations = permissions.canMergeConversations;
    this.canReplyToInboundConversations = permissions.canReplyToInboundConversations;
    this.canAccessContacts = permissions.canAccessContacts;
    this.canAccessBillingSettings = permissions.canAccessBillingSettings;
    this.canAccessAllConversations = permissions.canAccessAllConversations;
    this.canListenAndBargeOnCalls = permissions.canListenAndBargeOnCalls;
    this.canManageKnowledgeBaseContent = permissions.canManageKnowledgeBaseContent;
    this.canRemoveConversationSlas = permissions.canRemoveConversationSlas;
    this.canAccessReporting = permissions.canAccessReporting;
    this.canUsePersonalMacros = permissions.canUsePersonalMacros;
    this.canDeleteCallRecordings = permissions.canDeleteCallRecordings;
    this.canUseDataConnectors = permissions.canUseDataConnectors;
    this.canReassignConversationsOnAway = permissions.canReassignConversationsOnAway;
    this.canChangeTicketType = permissions.canChangeTicketType;
    this.canEditNotes = permissions.canEditNotes;
    this.canEditOwnProfile = permissions.canEditOwnProfile;
    this.canEditOtherProfiles = permissions.canEditOtherProfiles;
  }
}

export default class AdminWithPermissions extends AdminSummary {
  permissions: AdminPermissions;
  locale: string;
  teamIds: Array<number>;
  seats: Array<SeatType | undefined>;
  optedIntoCopilotWaitlist: boolean;
  count?: number;
  optedIntoInbox2?: boolean | null;
  adminStatus?: AdminStatus;
  isOperator?: boolean;
  teamAccessControl?: TeamAccessControlData;
  teammatePreferredLocale?: string;

  constructor(
    id: number,
    name: string,
    imageURL: string | null,
    permissions: AdminPermissions,
    seats: Array<SeatType | undefined>,
    teamIds: Array<number>,
    locale: string,
    optedIntoCopilotWaitlist: boolean,
    aliasEnabled?: boolean,
    aliasName?: string,
    aliasEmail?: string,
    isImpersonating?: boolean,
    count?: number | 0,
    optedIntoInbox2?: boolean | null,
    adminStatus?: AdminStatus,
    isOperator?: boolean,
    teamAccessControl?: TeamAccessControlData,
    teammatePreferredLocale?: string,
  ) {
    super(id, name, imageURL, aliasEnabled, aliasName, aliasEmail, isImpersonating);

    this.permissions = permissions;
    this.teamIds = teamIds;
    this.count = count;
    this.locale = locale;
    this.seats = seats;
    this.optedIntoInbox2 = optedIntoInbox2;
    this.optedIntoCopilotWaitlist = optedIntoCopilotWaitlist;
    this.adminStatus = adminStatus;
    this.isOperator = isOperator;
    this.teamAccessControl = teamAccessControl;
    this.teammatePreferredLocale = teammatePreferredLocale;
  }

  static deserialize(json: AdminWithPermissionsWireFormat) {
    let permissions = new AdminPermissions({
      conversationAccess: {
        accessType: json.permissions.conversation_access.access_type,
        assigneeBlockedList: json.permissions.conversation_access.assignee_blocked_list,
        includeUnassigned: json.permissions.conversation_access.include_unassigned,
      },
      roleId: json.permissions.role_id,
      hasInboxAccess: json.permissions.has_inbox_access,
      canManageTeammates: json.permissions.manage_teammates,
      canChangeAwayMode: json.permissions.teammates__away_mode__change,
      canManageTags: json.permissions.manage_tags,
      canManageMacros: json.permissions.manage_saved_replies,
      canCreateMacros: json.permissions.create_macros,
      canEditMacros: json.permissions.edit_macros,
      canDeleteMacros: json.permissions.delete_macros,
      canInboxAccessCopilot: json.permissions.inbox__access_copilot,
      canInboxConversationTranscriptsExport:
        json.permissions.inbox__conversation_transcripts__export,
      hasFullAccess: json.permissions.has_full_access,
      canManageInboxViews: json.permissions.manage_inbox_views,
      canManageInboxViewsPinnedColumns: json.permissions.manage_inbox_views_pinned_columns,
      canManageSharedCustomFolders: json.permissions.manage_shared_custom_folders,
      canAccessRealTimeDashboard: json.permissions.access_real_time_dashboard,
      canReassignConversations: json.permissions.reassign_conversations,
      canRedactConversationParts: json.permissions.redact_conversation_parts,
      canSendFromCustomAddresses: json.permissions.send_from_custom_addresses,
      canAccessWorkspaceSettings: json.permissions.access_workspace_settings,
      canManageEmojis: json.permissions.manage_emojis,
      canManageConversationParticipants: json.permissions.manage_conversation_participants,
      canManageConversationCreation: json.permissions.manage_conversation_creation,
      canMergeConversations: json.permissions.merge_conversations,
      canReplyToInboundConversations: json.permissions.reply_to_inbound_conversations,
      canAccessContacts: json.permissions.access_contacts,
      canAccessBillingSettings: json.permissions.access_billing_settings,
      canAccessAllConversations: json.permissions.can_access_all_conversations,
      canListenAndBargeOnCalls: json.permissions.calling__listening_and_barging,
      canDeleteCallRecordings: json.permissions.calling__delete_call_recordings,
      canManageKnowledgeBaseContent: json.permissions.manage_knowledge_base_content,
      canRemoveConversationSlas: json.permissions.remove_conversation_slas,
      canAccessReporting: json.permissions.access_reporting,
      canUsePersonalMacros: json.permissions.use_personal_macros,
      canUseDataConnectors: json.permissions.use_data_connectors,
      canReassignConversationsOnAway: json.permissions.reassign_conversations_on_away ?? true,
      canChangeTicketType: json.permissions.change_ticket_type,
      canEditNotes: json.permissions.edit_notes,
      canEditOwnProfile: json.permissions.edit_own_profile,
      canEditOtherProfiles: json.permissions.edit_other_profiles,
    });

    let adminStatus = new AdminStatus(
      json.admin_status?.away_mode_enabled,
      json.admin_status?.reassign_conversations,
      json.admin_status?.is_auto_changed,
      json.admin_status?.channel_availability,
    );

    return new AdminWithPermissions(
      json.id,
      json.name ?? REDACTED_ADMIN_NAME,
      json.image_url ?? REDACTED_ADMIN_URL,
      permissions,
      json.seats,
      json.team_ids,
      json.locale,
      json.copilot_waitlist_opt_in,
      json.alias_enabled,
      json.alias_name,
      json.alias_email,
      json.is_impersonating,
      json.count,
      json.inbox_2_opt_in,
      adminStatus,
      json.is_operator,
      json.team_access_control,
      json.teammate_preferred_locale,
    );
  }

  get hasVbp2CollaboratorSeat() {
    return (this.seats || []).includes(COLLABORATOR_SEAT_TYPE);
  }

  get hasPricing5LiteSeat() {
    return (this.seats || []).includes(PRICING_5_X_LITE_SEAT_TYPE);
  }

  get hasLimitedCopilotAccess() {
    return this.hasCopilotAccess && !this.hasCopilotSeat;
  }

  get hasUnlimitedCopilotAccess() {
    return this.hasCopilotAccess && this.hasCopilotSeat;
  }

  get hasCopilotAccess() {
    return this.permissions.canInboxAccessCopilot;
  }

  get hasCopilotSeat() {
    return this.seats.includes(COPILOT_SEAT_TYPE);
  }
}
